/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { isNotNil } from 'es-toolkit';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useRestartEndpoint } from '@fai/friendli-suite/features/products/dedicated-endpoints/endpoints/use-endpoint-restart';
import { useEndpointStatus } from '@fai/friendli-suite/features/products/dedicated-endpoints/endpoints/use-endpoint-status';
import { useDedicatedEndpointsTeamPlan } from '@fai/friendli-suite/features/products/dedicated-endpoints/plan/use-dedicated-endpoints-team-plan';
import { useDedicatedFreeCredit } from '@fai/friendli-suite/features/products/dedicated-endpoints/use-credit-remaining';
import { usePaymentState } from '@fai/friendli-suite/features/settings/team-settings/use-payment';
import { useTeamRole } from '@fai/friendli-suite/features/settings/team-settings/use-role';
import { DedicatedEndpointStatus } from '@fai/friendli-suite/generated/client/graphql';
import HeaderMessage from '@fai/friendli-suite/views/layout/header/items/header-message';
import { openAddPaymentModal } from '@fai/friendli-suite/views/team-settings/billing/payment-method/add-payment-modal';

import GlobalHeaderMessage from './global';

function TeamOwnerWithNoPaymentMessage() {
  const { t } = useTranslation();

  return (
    <HeaderMessage
      contentButton={
        <HeaderMessage.ContentActionButton handleOnClick={openAddPaymentModal}>
          {t('dedicated_endpoints.layout.add_payment_method')}
        </HeaderMessage.ContentActionButton>
      }
      variant="WARNING"
    >
      {t('dedicated_endpoints.layout.to_get_started_add_payment_method')}
    </HeaderMessage>
  );
}

function TeamMemberHasPaymentIssuesMessage() {
  const { t } = useTranslation();

  return (
    <HeaderMessage variant="WARNING">
      {t('dedicated_endpoints.layout.dedicated_endpoints_is_not_accessible')}
    </HeaderMessage>
  );
}

function SampleEndpointMessage() {
  const { t } = useTranslation();

  const { status: endpointStatus } = useEndpointStatus();
  const { restartEndpoint } = useRestartEndpoint();

  return (
    endpointStatus === DedicatedEndpointStatus.Ready && (
      <HeaderMessage
        contentButton={
          <HeaderMessage.ContentActionButton handleOnClick={restartEndpoint}>
            {t('dedicated_endpoints.project.start_endpoint')}
          </HeaderMessage.ContentActionButton>
        }
        variant="INFO"
      >
        {t('dedicated_endpoints.project.start_endpoint_message')}
      </HeaderMessage>
    )
  );
}

export default function DedicatedEndpointsHeaderMessage() {
  const { teamId = '', endpointId } = useParams();

  const { isTeamOwner, loading: roleLoading } = useTeamRole(teamId);
  const { isPaymentEmpty, isPaymentInvalid, loading: paymentLoading } = usePaymentState(teamId);
  const { plan: dedicatedEndpointsPlan, loading: planLoading } =
    useDedicatedEndpointsTeamPlan(teamId);
  const { isFreeCreditExist, loading: freeCreditLoading } = useDedicatedFreeCredit(teamId);

  if (roleLoading || paymentLoading || planLoading || freeCreditLoading) return null;

  // If endpoint detail page with ready state and free credit is exist, show this.
  if (isNotNil(endpointId) && isFreeCreditExist) {
    const sampleEndpointMessageComponent = <SampleEndpointMessage />;

    if (sampleEndpointMessageComponent) {
      return sampleEndpointMessageComponent;
    }
  }

  // If plan is ENTERPRISE, or has not tried trial yet, don't show message.
  if (
    dedicatedEndpointsPlan === 'ENTERPRISE' ||
    dedicatedEndpointsPlan === 'NONE_OWNER' ||
    dedicatedEndpointsPlan === 'NONE_MEMBER'
  ) {
    return null;
  }

  // If there is no payment method, show this for team owner.
  if (isTeamOwner && isPaymentEmpty && !isFreeCreditExist) {
    return <TeamOwnerWithNoPaymentMessage />;
  }

  // If there is an issue with a payment method, show this for team member.
  if (!isTeamOwner && isPaymentInvalid && !isFreeCreditExist) {
    return <TeamMemberHasPaymentIssuesMessage />;
  }

  return <GlobalHeaderMessage />;
}

DedicatedEndpointsHeaderMessage.displayName = 'DedicatedEndpointsHeaderMessage';
